import { useState } from "react";
import { Heart, Sparkles, Loader2, Download, Share2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface CaptureSparkReelProps {
  match: {
    id: string;
    match_source: string;
    matchedProfile: {
      id: string;
      name: string;
      photo_url: string | null;
    } | null;
    source_context?: Record<string, unknown> | null;
    compatibility?: {
      score: number;
    };
    created_at: string;
  };
  firstMessage?: string | null;
  onClose: () => void;
}

export const CaptureSparkReel = ({ match, firstMessage, onClose }: CaptureSparkReelProps) => {
  const [generating, setGenerating] = useState(false);
  const [reelUrl, setReelUrl] = useState<string | null>(null);

  const generateReel = async () => {
    if (!match.matchedProfile) return;

    setGenerating(true);
    try {
      // TODO: Implement actual reel generation using canvas/video API
      // For now, create a placeholder
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Generate poetic caption
      const context = match.source_context as Record<string, string> | null;
      let caption = "We sparked together ❤️";

      if (match.match_source === "global_connect") {
        const scene = context?.scene;
        const cityName = context?.city_name;
        if (scene && cityName) {
          caption = `We sparked in a ${cityName} ${scene} scene ❤️`;
        } else if (cityName) {
          caption = `Our global heartbeat across oceans — ${cityName} ❤️`;
        }
      } else if (match.match_source === "exhibition") {
        const boothName = context?.booth_name;
        const cityName = context?.city_name;
        if (boothName && cityName) {
          caption = `Our ${boothName} moment in ${cityName} turned into something real ❤️`;
        }
      } else if (match.match_source === "pop_balloon") {
        caption = "No swiping, just intention — our first spark ❤️";
      }

      // In production, this would:
      // 1. Create canvas with blurred-to-clear avatar transition
      // 2. Add source animation (balloon pop, booth spotlight, globe spin)
      // 3. Add compatibility percentage sparkle
      // 4. Add 5-second audio snippet if available (opt-in)
      // 5. Add poetic caption overlay
      // 6. Export as video file
      // 7. Upload to storage and return URL

      const mockReelUrl = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2YwZjlmYSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM2NjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5SZWVsIFByZXZpZXc8L3RleHQ+PC9zdmc+";
      setReelUrl(mockReelUrl);

      toast.success("Spark reel generated! ✨");
    } catch (error) {
      console.error("Error generating reel:", error);
      toast.error("Failed to generate reel. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleShare = async () => {
    if (!reelUrl) {
      await generateReel();
      return;
    }

    // TODO: Implement native share API or download
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Our Spark Story",
          text: "Check out our spark story on Spikee Bloom!",
          url: reelUrl,
        });
        toast.success("Shared successfully! ✨");
      } catch (error) {
        // User cancelled or error
        console.error("Share error:", error);
      }
    } else {
      // Fallback: download
      const link = document.createElement("a");
      link.href = reelUrl;
      link.download = `spark-reel-${match.id}.mp4`;
      link.click();
      toast.success("Reel downloaded! ✨");
    }
  };

  if (!match.matchedProfile) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl p-6 max-w-md w-full shadow-2xl border border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Capture Our Spark
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Generate a beautiful, shareable reel of your connection story
        </p>

        {/* Preview area */}
        <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5 mb-4 border border-border">
          {reelUrl ? (
            <img src={reelUrl} alt="Reel preview" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <Heart className="w-12 h-12 text-primary/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Reel preview will appear here</p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            onClick={reelUrl ? handleShare : generateReel}
            disabled={generating}
            className="flex-1"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : reelUrl ? (
              <>
                <Share2 className="w-4 h-4 mr-2" />
                Share Reel
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Reel
              </>
            )}
          </Button>
          {reelUrl && (
            <Button
              variant="outline"
              onClick={() => {
                const link = document.createElement("a");
                link.href = reelUrl;
                link.download = `spark-reel-${match.id}.mp4`;
                link.click();
                toast.success("Reel downloaded! ✨");
              }}
            >
              <Download className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

